import { useRef, useState, useEffect, type CSSProperties } from 'react';

const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:8080';

// The API serves some images from its own paths (e.g. the venue photo proxy,
// /api/venues/:id/photo). Those arrive as root-relative paths, and the app is
// on a different origin from the API — left alone, the browser would request
// them from app.preminade.com and 404. Absolute URLs (S3 uploads) pass through.
function resolveSrc(src?: string | null): string | undefined {
  if (!src) return undefined;
  return src.startsWith('/api/') ? `${API_BASE}${src}` : src;
}

/**
 * Drop-in <img> replacement: invisible until decoded, then fades in.
 * The placeholder colour is rendered as the img's own background, so no
 * wrapper element is added and existing layouts stay untouched.
 */
export function Img({ src, alt = '', style, placeholder, draggable, className }: {
  src?: string | null;
  alt?: string;
  style?: CSSProperties;
  placeholder?: string;   // e.g. t.paperAlt
  draggable?: boolean;
  className?: string;
}) {
  const ref = useRef<HTMLImageElement>(null);
  const [loaded, setLoaded] = useState(false);

  // Cached images can be `complete` before onLoad binds — check on mount,
  // and reset when src changes so back-navigation shows them instantly.
  useEffect(() => {
    setLoaded(!!ref.current?.complete);
  }, [src]);

  return (
    <img
      ref={ref}
      src={resolveSrc(src)}
      alt={alt}
      draggable={draggable}
      className={className}
      onLoad={() => setLoaded(true)}
      style={{
        ...style,
        background: placeholder,
        opacity: loaded ? 1 : 0,
        transition: 'opacity 300ms ease',
      }}
    />
  );
}
